import React, { useState, useEffect, useMemo } from 'react';
import { Button, Table, Badge, Spinner, Alert, Card, Modal, Form, InputGroup } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import ProgramForm from './ProgramForm';
import StartProgramModal from './StartProgramModal';
import ProgramPrintPreview from './ProgramPrintPreview';
import ImportProgramModal from './ImportProgramModal';

function ProgramManager({ onBack }) {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [startingProgram, setStartingProgram] = useState(null);
  const [showStartProgramModal, setShowStartProgramModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);

  // Duplicate state (id of the program currently being duplicated)
  const [duplicatingProgramId, setDuplicatingProgramId] = useState(null);

  // Export state
  const [exportingProgram, setExportingProgram] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  // Search, sort, collapse state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [collapsedSections, setCollapsedSections] = useState({});

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workoutAPI.fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Tokenized search
  const tokenize = (query) => query.toLowerCase().split(/\s+/).filter(Boolean);

  // Filter and sort programs
  const processedPrograms = useMemo(() => {
    let filtered = programs;

    // Apply search filter
    if (searchQuery.trim()) {
      const tokens = tokenize(searchQuery);
      filtered = programs.filter(p => {
        const searchable = `${p.name} ${p.description || ''}`.toLowerCase();
        return tokens.every(token => searchable.includes(token));
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      } else if (sortColumn === 'count') {
        aVal = a.workout_count || 0;
        bVal = b.workout_count || 0;
      }
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [programs, searchQuery, sortColumn, sortDirection]);

  // Separate programs into templates and started copies
  const groupedPrograms = useMemo(() => {
    const templatePrograms = processedPrograms.filter(p => p.is_template === 1 || p.is_template === true);
    const startedPrograms = processedPrograms.filter(p => p.is_template === 0 || p.is_template === false);
    return { templates: templatePrograms, started: startedPrograms };
  }, [processedPrograms]);

  // Sort handlers
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Collapse handlers
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const collapseAll = () => {
    setCollapsedSections({ templates: true, started: true });
  };

  const expandAll = () => setCollapsedSections({});

  const handleCreateNew = () => {
    setEditingProgram(null);
    setShowForm(true);
  };

  const handleEdit = async (program) => {
    try {
      const fullProgram = await workoutAPI.fetchProgramById(program.id);
      setEditingProgram(fullProgram);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching program details:', err);
      setError('Failed to load program details. Please try again.');
    }
  };

  const handleSave = async (formData, autoSavedProgramId = null) => {
    setError(null);
    try {
      if (editingProgram) {
        await workoutAPI.updateProgram(editingProgram.id, formData);
      } else if (autoSavedProgramId) {
        await workoutAPI.updateProgram(autoSavedProgramId, formData);
      } else {
        await workoutAPI.createProgram(formData);
      }
      setShowForm(false);
      setEditingProgram(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error saving program:', err);
      setError('Failed to save program. Please try again.');
    }
  };

  const handleDeleteClick = (program) => {
    setProgramToDelete(program);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete) return;

    setError(null);
    try {
      await workoutAPI.deleteProgram(programToDelete.id);
      setShowDeleteModal(false);
      setProgramToDelete(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      setError('Failed to delete program. Please try again.');
      setShowDeleteModal(false);
      setProgramToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProgramToDelete(null);
  };

  const handleStartProgram = (program) => {
    setStartingProgram(program);
    setShowStartProgramModal(true);
  };

  const handleStartProgramSuccess = async () => {
    setSuccessMessage(`Program "${startingProgram.name}" started successfully! Go to the landing page to continue.`);
    setShowStartProgramModal(false);
    setStartingProgram(null);
    await fetchPrograms();
  };

  const handleDuplicateProgram = async (program) => {
    setDuplicatingProgramId(program.id);
    setError(null);
    try {
      const result = await workoutAPI.duplicateProgram(program.id);
      setSuccessMessage(`Duplicated "${program.name}" as "${result.name}". You can now edit the copy independently.`);
      await fetchPrograms();
    } catch (err) {
      console.error('Error duplicating program:', err);
      setError('Failed to duplicate program. Please try again.');
    } finally {
      setDuplicatingProgramId(null);
    }
  };

  const handleExportProgram = async (program) => {
    setIsLoadingExport(true);
    setError(null);
    try {
      const fullProgram = await workoutAPI.fetchProgramById(program.id);
      setExportingProgram(fullProgram);
      setShowExportModal(true);
    } catch (err) {
      console.error('Error fetching program for export:', err);
      setError('Failed to load program for export. Please try again.');
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setExportingProgram(null);
  };

  const handleImported = async (result) => {
    setShowImportModal(false);
    if (result) {
      const newExercises = result.created_exercises?.length || 0;
      setSuccessMessage(
        `Imported "${result.name}" with ${result.workout_count} workout${result.workout_count !== 1 ? 's' : ''}` +
        (newExercises > 0 ? ` and ${newExercises} new exercise${newExercises !== 1 ? 's' : ''}.` : '.')
      );
    }
    await fetchPrograms();
  };

  // Render sortable header
  const SortableHeader = ({ column, label }) => (
    <th
      onClick={() => handleSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title={`Sort by ${label}`}
    >
      {label}
      {sortColumn === column ? (
        <i className={`fas fa-chevron-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
      ) : (
        <i className="fas fa-chevron-down ms-1" style={{ opacity: 0.3 }}></i>
      )}
    </th>
  );

  // Render program row
  const ProgramRow = ({ program, showStartButton }) => (
    <tr key={program.id}>
      <td><strong>{program.name}</strong></td>
      <td>
        <span className="text-muted">
          {program.description || 'No description'}
        </span>
      </td>
      <td>
        <Badge bg="info">
          {program.workout_count || 0} workout{(program.workout_count || 0) !== 1 ? 's' : ''}
        </Badge>
      </td>
      <td>
        <div className="btn-group" role="group">
          {showStartButton && (
            <Button
              variant="success"
              size="sm"
              onClick={() => handleStartProgram(program)}
              disabled={startingProgram === program.id}
              title="Creates your own copy of this program and activates it so you can start tracking workouts"
            >
              {startingProgram === program.id ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                  Starting...
                </>
              ) : (
                <>
                  <i className="fas fa-play me-1"></i>
                  Start
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleDuplicateProgram(program)}
            disabled={duplicatingProgramId !== null}
            title="Duplicate program (creates an editable copy)"
          >
            {duplicatingProgramId === program.id ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            ) : (
              <i className="fas fa-copy"></i>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleExportProgram(program)}
            disabled={isLoadingExport}
            title="Export to Word"
          >
            <i className="fas fa-file-export"></i>
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(program)}
            title="Edit program"
          >
            <i className="fas fa-edit"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteClick(program)}
            title="Delete program"
          >
            <i className="fas fa-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading programs...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading programs...</p>
      </div>
    );
  }

  const hasResults = processedPrograms.length > 0;
  const hasBothSections = groupedPrograms.templates.length > 0 && groupedPrograms.started.length > 0;

  return (
    <div className="program-manager">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <i className="fas fa-calendar-week me-2"></i>
            Workout Programs
          </h2>
          <p className="text-muted">Create and manage your multi-week training programs</p>
        </div>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {/* Toolbar: Create + Search + Collapse Controls */}
      <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
        <Button variant="primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>
          Create New Program
        </Button>

        <Button
          variant="outline-primary"
          onClick={() => setShowImportModal(true)}
          title="Import a program from a JSON file"
        >
          <i className="fas fa-file-import me-2"></i>
          Import Program
        </Button>

        <InputGroup style={{ maxWidth: '300px' }}>
          <InputGroup.Text>
            <i className="fas fa-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search programs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
              <i className="fas fa-times"></i>
            </Button>
          )}
        </InputGroup>

        {programs.length > 0 && hasBothSections && (
          <div className="btn-group">
            <Button variant="outline-secondary" size="sm" onClick={collapseAll} title="Collapse all sections">
              <i className="fas fa-compress-alt me-1"></i>
              Collapse All
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={expandAll} title="Expand all sections">
              <i className="fas fa-expand-alt me-1"></i>
              Expand All
            </Button>
          </div>
        )}

        {searchQuery && (
          <Badge bg="secondary" className="py-2 px-3">
            {processedPrograms.length} result{processedPrograms.length !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {programs.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-calendar-week fa-4x text-muted mb-3"></i>
            <h4>No Programs Yet</h4>
            <p className="text-muted">
              Create your first workout program to organize your training into a structured plan
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button variant="primary" onClick={handleCreateNew}>
                <i className="fas fa-plus me-2"></i>
                Create Program
              </Button>
              <Button variant="outline-primary" onClick={() => setShowImportModal(true)}>
                <i className="fas fa-file-import me-2"></i>
                Import Program
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : !hasResults ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-search fa-3x text-muted mb-3"></i>
            <h4>No Matching Programs</h4>
            <p className="text-muted">
              No programs match "{searchQuery}"
            </p>
            <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="programs-list">
          {/* Template Programs Section */}
          {groupedPrograms.templates.length > 0 && (
            <Card className="mb-4">
              <Card.Header
                onClick={() => toggleSection('templates')}
                style={{ cursor: 'pointer' }}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <h5 className="mb-0">
                    <i className={`fas fa-chevron-${collapsedSections['templates'] ? 'right' : 'down'} me-2`}></i>
                    <i className="fas fa-file-alt me-2"></i>
                    Program Templates
                    <Badge bg="secondary" className="ms-2">{groupedPrograms.templates.length}</Badge>
                  </h5>
                  <small className="text-muted">Your program templates - click "Start" to begin tracking</small>
                </div>
              </Card.Header>
              {!collapsedSections['templates'] && (
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <SortableHeader column="name" label="Name" />
                        <th>Description</th>
                        <SortableHeader column="count" label="Workouts" />
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedPrograms.templates.map((program) => (
                        <ProgramRow key={program.id} program={program} showStartButton={true} />
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              )}
            </Card>
          )}

          {/* Started Programs Section */}
          {groupedPrograms.started.length > 0 && (
            <Card className="mb-4">
              <Card.Header
                onClick={() => toggleSection('started')}
                style={{ cursor: 'pointer' }}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <h5 className="mb-0">
                    <i className={`fas fa-chevron-${collapsedSections['started'] ? 'right' : 'down'} me-2`}></i>
                    <i className="fas fa-running me-2"></i>
                    Started Programs
                    <Badge bg="success" className="ms-2">{groupedPrograms.started.length}</Badge>
                  </h5>
                  <small className="text-muted">Programs you've started - these are your active tracking copies</small>
                </div>
              </Card.Header>
              {!collapsedSections['started'] && (
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <SortableHeader column="name" label="Name" />
                        <th>Description</th>
                        <SortableHeader column="count" label="Workouts" />
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedPrograms.started.map((program) => (
                        <ProgramRow key={program.id} program={program} showStartButton={false} />
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-danger me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete the program <strong>"{programToDelete?.name}"</strong>?
          </p>
          <p className="text-muted mt-2 mb-0">
            This action cannot be undone. All workouts in this program will be removed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <i className="fas fa-trash me-2"></i>
            Delete Program
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Start Program Modal */}
      {showStartProgramModal && startingProgram && (
        <StartProgramModal
          program={startingProgram}
          onClose={() => {
            setShowStartProgramModal(false);
            setStartingProgram(null);
          }}
          onSuccess={handleStartProgramSuccess}
        />
      )}

      {showForm && (
        <ProgramForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setEditingProgram(null);
            setError(null);
          }}
          onSave={handleSave}
          program={editingProgram}
          error={error}
        />
      )}

      {/* Export Modal */}
      {showExportModal && exportingProgram && (
        <ProgramPrintPreview
          program={exportingProgram}
          onClose={handleCloseExportModal}
        />
      )}

      {/* Import Modal */}
      <ImportProgramModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={handleImported}
      />
    </div>
  );
}

export default ProgramManager;
